import type { MouseEvent, ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Lockup, Page } from '@beyond-js/ui/react';
import wordmark from '@brand/beyond-logo-wordmark.svg';
import { useI18n } from '@cdn/i18n';
import { useSession } from '@cdn/identity';
import { Paths } from '@cdn/navigation';
import { AsideProvider, OutageNotices, OutageScope, useAsideHost } from '@cdn/ui';
import { Bar } from './bar';
import { Layout } from './layout';
import { Palette } from './palette';
import { Returned } from './returned';
import { Sections } from './sections';
import styles from './styles.module.css';
import { LanguageSelector, PublicLinks, ThemeToggle } from './tools';
import { Way } from './way';

/**
 * Layout of the management views: the family bar, CDN's sections and one family `Page` (decision D52), laid out
 * by the family's shell (`bui-shell`). From the sidebar's cut on the sections stand beside the page, flush under
 * the bar; below it they are a product row above it whose drawer holds the same links. Without an organization
 * there are no sections. The page runs edge to edge and starts its content one gutter after the sections, never
 * centred; its template and width tier follow the address (`Layout`), and a page another product opened starts
 * with the arrival line (`Way`). A breadcrumb the family header draws is a plain link: it is routed here.
 * The ⌘K palette's host stays outside the shell: every other child of the shell is a column that shares its
 * width, so a host there took half the page's region and pushed its content off the start edge. The page's
 * outages are held here (decision D163) and said once by its header, or at its top when it has none. A resource
 * page's conditions stand in the page's raised side panel, which the page claims with `Aside`.
 */
export function Frame({ children }: { children: ReactNode }) {
	const { t } = useI18n();
	const { organization } = useSession();
	const { pathname } = useLocation();
	const navigate = useNavigate();
	const paths = organization && new Paths(organization.organization);
	const layout = new Layout(pathname);
	// A resource page's conditions stand in a raised side panel the page claims (`Aside` of `@cdn/ui`)
	const aside = useAsideHost();

	const follow = (event: MouseEvent<HTMLElement>) => {
		if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
		const link = (event.target as Element).closest?.('.bui-crumbs a');
		if (!(link instanceof HTMLAnchorElement)) return;
		const url = new URL(link.href);
		if (url.origin !== window.location.origin) return;
		event.preventDefault();
		navigate(`${url.pathname}${url.search}${url.hash}`);
	};

	return (
		<div className="flex min-h-screen flex-col bg-page text-ink">
			<a href="#main" className={styles.skip}>
				{t('shell.skip')}
			</a>
			<Returned />
			<Bar />
			<div className="bui-shell flex-1">
				{paths && <Sections paths={paths} />}
				<main id="main" tabIndex={-1} className="flex min-w-0 flex-1" onClick={follow}>
					<OutageScope>
						<AsideProvider holder={aside.holder}>
							<Page
								template={layout.template}
								width={layout.width}
								arrival={<Way />}
								aside={aside.claim ? <div ref={aside.place} className="flex min-w-0 flex-col gap-6" /> : undefined}
								label={aside.claim?.label}
								raised={Boolean(aside.claim)}
							>
								<OutageNotices fallback />
								{children}
							</Page>
						</AsideProvider>
					</OutageScope>
				</main>
			</div>
			{paths && <Palette paths={paths} />}
		</div>
	);
}

/**
 * Layout of the views that need no session, such as signing in: the family lockup alone, with the public
 * links, the language and the theme. Signed out there is no family bar.
 */
export function PublicFrame({ children }: { children: ReactNode }) {
	const { t } = useI18n();
	return (
		<div className="flex min-h-screen flex-col bg-page text-ink">
			<a href="#main" className={styles.skip}>
				{t('shell.skip')}
			</a>
			<header className={styles.header}>
				<div className="mx-auto flex max-w-[90rem] flex-wrap items-center gap-x-6 gap-y-2 px-4 py-3 sm:px-6">
					<Link to="/" translate="no" className="flex rounded-control text-ink no-underline [--bui-lockup-height:24px]">
						<Lockup src={wordmark} name={t('shell.product.short')} alt={t('shell.brand')} />
					</Link>
					<nav aria-label={t('shell.tools')} className="-ml-2.5 flex flex-wrap items-center gap-1 sm:-mr-2.5 sm:ml-auto">
						<PublicLinks />
						<LanguageSelector />
						<ThemeToggle />
					</nav>
				</div>
			</header>
			<main id="main" tabIndex={-1} className="flex-1 px-4 sm:px-6">
				{children}
			</main>
		</div>
	);
}
